import React, { useEffect, useState, forwardRef } from 'react';

import type { SiteModel } from '../models/DataAdminTypes';
import DataAdminServiceFactory from '../services/DataAdminServiceFactory';

// --- Component
export type SiteDropdownProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  getAccessToken: () => Promise<string>;
  label?: string;
  value?: string; // selected site id
  onSiteSelected?: (value: SiteModel) => void;
  placeholder?: string; // default option label
  helperText?: string;
  env: string;
};

const ChevronDown = (): React.JSX.Element => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 stroke-slate-500">
    <path d="M6 8l4 4 4-4" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Forward ref for form libs (React Hook Form, etc.)
const SiteDropdown = forwardRef<HTMLSelectElement, SiteDropdownProps>(
  (
    {
      getAccessToken,
      label = 'Site',
      value,
      onSiteSelected,
      placeholder,
      disabled,
      helperText,
      className,
      env,
      ...rest
    },
    ref
  ) => {
    const [selectedValue, setSelectedValue] = useState<string>(value ?? '');
    const [sites, setSites] = useState<SiteModel[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const dataAdminService = new DataAdminServiceFactory().create(env); // Use 'test' mode for mock data

      const fetchSites = async (): Promise<void> => {
        try {
          setError(null);
          setLoading(true);

          const sites = await dataAdminService.getSites(await getAccessToken());
          setSites(sites);
        } catch (e) {
          console.error('Failed to fetch sites:', e);
          setError('Failed to load sites.');
        } finally {
          setLoading(false);
        }
      };

      fetchSites();
    }, [env, getAccessToken]);

    const handleChange: React.ChangeEventHandler<HTMLSelectElement> = e => {
      setError(null);
      setSelectedValue(e.target.value);
      const selectedSite = sites.find(s => s.siteCode === e.target.value);
      if (!selectedSite) {
        setError('Selected site not found.');
        return;
      }
      onSiteSelected?.(selectedSite);
    };

    const isDisabled = disabled || loading;

    return (
      <div className={className}>
        {/* Label */}
        <label htmlFor='select-site-control' className="mb-1 block text-sm font-medium text-slate-700">
          {label}
          {<span className="ml-1 text-rose-600">*</span>}
        </label>

        {/* Select wrapper for chevron */}
        <div className="relative">
          <select
            id='select-site-control'
            ref={ref}
            value={selectedValue}
            onChange={handleChange}
            disabled={isDisabled}
            aria-busy={loading}
            aria-invalid={!!error}
            className={[
              'block w-full appearance-none rounded-xl border bg-white px-3 py-2.5 text-sm shadow-sm',
              'border-slate-300 text-slate-800 placeholder-slate-400',
              'focus:outline-none focus:ring-2 focus:ring-slate-400',
              isDisabled ? 'cursor-not-allowed bg-slate-50 text-slate-500' : '',
              error ? 'border-rose-300 ring-rose-200' : '',
            ].join(' ')}
            {...rest}
          >
            <option value="" disabled={true}>
              {loading ? 'Loading sites…' : placeholder}
            </option>
            {sites.map(s => (
              <option key={s.siteCode} value={s.siteCode}>
                {s.siteName}
              </option>
            ))}
          </select>

          <ChevronDown />
        </div>

        {/* Helper / status text */}
        {helperText && selectedValue === '' && !error && (
          <p className="mt-1 text-xs text-slate-500">{helperText}</p>
        )}

        {loading && (
          <p className="mt-1 text-xs text-slate-500">Loading sites…</p>
        )}

        {error && (
          <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
);

SiteDropdown.displayName = 'SiteDropdown';

export default SiteDropdown;
